import type { EChartsCoreOption } from "echarts/core";
import type {
  OpponentChallengeBand,
  PhaseErrorRate,
  RatePosteriorSummary,
  TimeUse,
  TrendForecastPoint,
  TrendSeriesPoint,
} from "@chessla/shared";
import { conciseOpeningName } from "@/shared/utils/chess";

const INK = "#0a0a0a",
  CHARCOAL = "#292723",
  PAPER = "#f0ece2",
  RED = "#ff3d00",
  MUTED = "#98968f";
const date = (value: string | number) =>
  new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));
const percent = (value: number) => Math.round(value * 100);
type ForecastCalibration = { holdoutSize: number; coverage: number };
const tooltipText = (value: unknown) =>
  String(value).replace(
    /[&<>"']/g,
    (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]!,
  );
const trendTooltip = (raw: unknown, calibration?: ForecastCalibration) => {
  const params = Array.isArray(raw) ? raw : [raw];
  const rows = params.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const item = entry as { seriesName?: unknown; data?: unknown };
    if (item.seriesName !== "Observed" && item.seriesName !== "Forecast") return [];
    const datum =
      item.data && typeof item.data === "object"
        ? (item.data as { value?: unknown; low?: unknown; high?: unknown })
        : null;
    const data = datum?.value;
    if (!Array.isArray(data) || data.length < 2 || typeof data[0] !== "string" || typeof data[1] !== "number")
      return [];
    const range =
      item.seriesName === "Forecast" && typeof datum?.low === "number" && typeof datum.high === "number"
        ? `<br/>Nominal model range (not a guarantee): ${datum.low}–${datum.high}${
            calibration
              ? `<br/>Measured held-out coverage: ${percent(calibration.coverage)}% (${calibration.holdoutSize} games)`
              : ""
          }`
        : "";
    return [{ when: data[0], line: `${item.seriesName}: ${data[1]}${range}` }];
  });
  return rows.length ? `${date(rows[0].when)}<br/>${rows.map((row) => row.line).join("<br/>")}` : "";
};
const tooltipData = (raw: unknown): Record<string, unknown> =>
  raw && typeof raw === "object" && "data" in raw && (raw as { data?: unknown }).data && typeof raw.data === "object"
    ? (raw as { data: Record<string, unknown> }).data
    : {};
const signed = (value: number) => `${value >= 0 ? "+" : ""}${value}`;
const base = (description: string): EChartsCoreOption => ({
  animationDuration: 700,
  animationEasing: "cubicOut",
  aria: { show: true, description },
  backgroundColor: "transparent",
  textStyle: { color: PAPER, fontFamily: "'JetBrains Mono', 'Fira Code', monospace" },
  tooltip: {
    trigger: "axis",
    confine: true,
    renderMode: "html",
    backgroundColor: INK,
    borderColor: RED,
    borderWidth: 1,
    borderRadius: 0,
    padding: [12, 14],
    extraCssText:
      "box-shadow:5px 5px 0 rgba(255,61,0,.28);letter-spacing:.04em;text-transform:uppercase;max-width:300px;white-space:normal",
    textStyle: {
      color: PAPER,
      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
      fontSize: 11,
      fontWeight: 700,
      lineHeight: 18,
    },
  },
});
const axes = () => ({
  xAxis: {
    type: "time",
    boundaryGap: false,
    axisLine: { lineStyle: { color: MUTED } },
    axisTick: { show: false },
    axisLabel: { color: MUTED, formatter: (value: number) => date(value), hideOverlap: true },
    splitLine: { show: false },
  },
  yAxis: {
    type: "value",
    scale: true,
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: MUTED },
    splitLine: { show: false },
  },
});

export const buildContinuousTrendOption = ({
  label,
  valueLabel,
  series,
  forecast,
}: {
  label: string;
  valueLabel: string;
  series: TrendSeriesPoint[];
  forecast?: { today: string; calibration: ForecastCalibration; series: TrendForecastPoint[] };
}): EChartsCoreOption => {
  const displayValue = (value: number) => (valueLabel.toLowerCase().includes("rating") ? Math.round(value) : value);
  const observed = series.map((point) => ({
    value: [point.playedAt, displayValue(point.value)] as [string, number],
    gameId: point.gameId,
    name: `${date(point.playedAt)} · ${valueLabel} ${displayValue(point.value)}`,
  }));
  const projected = forecast?.series ?? [];
  const forecastData = projected.map((point) => ({
    value: [point.playedAt, displayValue(point.mean)] as [string, number],
    low: displayValue(point.interval.low),
    high: displayValue(point.interval.high),
    name: `${date(point.playedAt)} · projected ${valueLabel} ${displayValue(point.mean)}`,
  }));
  const bridge = observed.length && forecastData.length ? [observed.at(-1)!, forecastData[0]] : [];
  const lower = projected.map((point) => [point.playedAt, displayValue(point.interval.low)]);
  const spread = projected.map((point) => [
    point.playedAt,
    displayValue(point.interval.high) - displayValue(point.interval.low),
  ]);
  const measuredCoverage = forecast
    ? ` Measured held-out coverage ${percent(forecast.calibration.coverage)}% (${forecast.calibration.holdoutSize} games).`
    : "";
  return {
    ...base(`${label}. Observed games, expected path, and Nominal model range (not a guarantee).${measuredCoverage}`),
    ...axes(),
    tooltip: {
      ...(base(label).tooltip as object),
      formatter: (raw: unknown) => trendTooltip(raw, forecast?.calibration),
      axisPointer: {
        type: "line",
        snap: false,
        lineStyle: { color: RED, type: "dashed", width: 1.5 },
        label: { show: false },
      },
    },
    grid: { left: 54, right: 26, top: 26, bottom: 48, containLabel: false },
    dataZoom: [{ type: "inside", xAxisIndex: 0, filterMode: "none", zoomOnMouseWheel: "shift" }],
    toolbox: {
      right: 8,
      top: 0,
      iconStyle: { borderColor: PAPER },
      emphasis: { iconStyle: { borderColor: RED } },
      feature: {
        dataZoom: { yAxisIndex: "none", title: { zoom: "Zoom", back: "Undo zoom" } },
        restore: { title: "Reset chart" },
      },
    },
    series: [
      {
        name: "Observed",
        type: "line",
        data: observed,
        dimensions: ["Date", valueLabel],
        encode: { x: "Date", y: valueLabel, tooltip: [valueLabel] },
        smooth: 0.24,
        smoothMonotone: "x",
        showSymbol: true,
        symbol: "rect",
        symbolSize: 7,
        lineStyle: { color: PAPER, width: 2.5 },
        itemStyle: { color: PAPER, borderColor: PAPER, borderWidth: 1 },
        emphasis: { scale: 1.7, itemStyle: { color: RED } },
        z: 5,
      },
      {
        name: "Observed to forecast",
        type: "line",
        data: bridge,
        smooth: 0.24,
        smoothMonotone: "x",
        showSymbol: false,
        symbol: "none",
        lineStyle: { color: PAPER, width: 2.5 },
        tooltip: { show: false },
        silent: true,
        z: 5,
      },
      {
        name: "Interval lower",
        type: "line",
        data: lower,
        stack: "forecast-band",
        symbol: "none",
        lineStyle: { opacity: 0 },
        areaStyle: { opacity: 0 },
        tooltip: { show: false },
        silent: true,
      },
      {
        name: "Nominal model range",
        type: "line",
        data: spread,
        stack: "forecast-band",
        symbol: "none",
        lineStyle: { opacity: 0 },
        areaStyle: { color: "rgba(255,61,0,.382)" },
        tooltip: { show: false },
        silent: true,
      },
      {
        name: "Forecast",
        type: "line",
        data: forecastData,
        smooth: 0.24,
        smoothMonotone: "x",
        showSymbol: true,
        symbol: "rect",
        symbolSize: 7,
        lineStyle: { color: RED, width: 2.5, type: "dashed" },
        itemStyle: { color: RED, borderColor: RED },
        markLine: projected[0]
          ? {
              silent: true,
              symbol: "none",
              label: { show: false },
              lineStyle: { color: RED, type: "dashed", width: 2 },
              data: [{ xAxis: projected[0].playedAt }],
            }
          : undefined,
        z: 6,
      },
    ],
  };
};

export const buildRateOption = ({
  label,
  prior,
  recent,
}: {
  label: string;
  prior: RatePosteriorSummary;
  recent: RatePosteriorSummary;
}): EChartsCoreOption => ({
  ...base(`${label}. Earlier and recent posterior means with 95 percent intervals.`),
  tooltip: {
    ...(base(label).tooltip as object),
    trigger: "item",
    formatter: (raw: unknown) => {
      const item = tooltipData(raw);
      return `${tooltipText(item.label ?? label)}<br/>Mean: ${tooltipText(item.value ?? 0)}%<br/>95%: ${tooltipText(item.low ?? 0)}–${tooltipText(item.high ?? 0)}%<br/>${tooltipText(item.events ?? 0)} errors / ${tooltipText(item.total ?? 0)} decisions`;
    },
  },
  grid: { left: 92, right: 30, top: 18, bottom: 30 },
  xAxis: {
    type: "value",
    min: 0,
    max: 100,
    axisLabel: { color: MUTED, formatter: "{value}%" },
    axisLine: { lineStyle: { color: MUTED } },
    splitLine: { show: false },
  },
  yAxis: {
    type: "category",
    data: ["EARLIER", "RECENT"],
    axisLabel: { color: PAPER, fontWeight: 800 },
    axisLine: { show: false },
    axisTick: { show: false },
  },
  series: [
    {
      name: "Posterior mean",
      type: "bar",
      data: [
        {
          label: "Earlier",
          value: percent(prior.mean),
          low: percent(prior.interval.low),
          high: percent(prior.interval.high),
          events: prior.events,
          total: prior.total,
          itemStyle: { color: PAPER },
        },
        {
          label: "Recent",
          value: percent(recent.mean),
          low: percent(recent.interval.low),
          high: percent(recent.interval.high),
          events: recent.events,
          total: recent.total,
          itemStyle: { color: RED },
        },
      ],
      barWidth: 18,
      label: { show: true, position: "right", color: PAPER, formatter: "{c}%" },
    },
    {
      name: "95% interval",
      type: "custom",
      data: [
        [0, percent(prior.interval.low), percent(prior.interval.high)],
        [1, percent(recent.interval.low), percent(recent.interval.high)],
      ],
      tooltip: { show: false },
      renderItem: (
        _params: unknown,
        api: { value: (index: number) => number; coord: (point: [number, number]) => [number, number] },
      ) => {
        const category = api.value(0),
          low = api.coord([api.value(1), category]),
          high = api.coord([api.value(2), category]);
        return {
          type: "group",
          children: [
            {
              type: "line",
              shape: { x1: low[0], y1: low[1], x2: high[0], y2: high[1] },
              style: { stroke: MUTED, lineWidth: 2 },
            },
            {
              type: "line",
              shape: { x1: low[0], y1: low[1] - 5, x2: low[0], y2: low[1] + 5 },
              style: { stroke: MUTED, lineWidth: 2 },
            },
            {
              type: "line",
              shape: { x1: high[0], y1: high[1] - 5, x2: high[0], y2: high[1] + 5 },
              style: { stroke: MUTED, lineWidth: 2 },
            },
          ],
        };
      },
    },
  ],
});

export const buildChallengeOption = (bands: OpponentChallengeBand[]): EChartsCoreOption => ({
  ...base("Games by opponent rating challenge, with score and average rating gap in the tooltip."),
  tooltip: {
    ...(base("challenge").tooltip as object),
    trigger: "item",
    formatter: (raw: unknown) => {
      const item = tooltipData(raw);
      return `${tooltipText(item.label ?? "Opponent band")}<br/>${tooltipText(item.value ?? 0)} games · ${tooltipText(item.share ?? 0)}%<br/>Score: ${tooltipText(item.score ?? 0)}%<br/>Average rating gap: ${signed(typeof item.gap === "number" ? item.gap : 0)}`;
    },
  },
  legend: { bottom: 0, textStyle: { color: MUTED } },
  grid: { left: 24, right: 24, top: 30, bottom: 52 },
  xAxis: { type: "value", show: false },
  yAxis: {
    type: "category",
    data: ["GAMES"],
    axisLabel: { color: PAPER, fontWeight: 800 },
    axisLine: { show: false },
    axisTick: { show: false },
  },
  series: bands.map((band) => {
    const label = band.band === "lower" ? "Lower-rated" : band.band === "similar" ? "Similar-rated" : "Stronger";
    return {
      name: label,
      type: "bar",
      stack: "games",
      data: [
        {
          label,
          value: band.games,
          share: percent(band.share),
          score: percent(band.score),
          gap: band.averageRatingGap,
        },
      ],
      itemStyle: { color: band.band === "stronger" ? RED : band.band === "similar" ? PAPER : MUTED },
      label: {
        show: band.share >= 0.16,
        position: "inside",
        color: INK,
        fontWeight: 900,
        formatter: `${percent(band.share)}%`,
      },
    };
  }),
});

export const buildPhaseOption = (phases: PhaseErrorRate[]): EChartsCoreOption => ({
  ...base("Error-rate posterior by opening, middlegame, and endgame, including decision samples."),
  tooltip: {
    ...(base("phase").tooltip as object),
    trigger: "item",
    formatter: (raw: unknown) => {
      const item = tooltipData(raw);
      const interval = Array.isArray(item.interval) ? item.interval : [];
      return `${tooltipText(item.phase ?? "Phase")}<br/>Error rate: ${tooltipText(Array.isArray(item.value) ? item.value[2] : 0)}%<br/>95%: ${tooltipText(interval[0] ?? "—")}–${tooltipText(interval[1] ?? "—")}%<br/>${tooltipText(item.decisions ?? 0)} decisions / ${tooltipText(item.games ?? 0)} games`;
    },
  },
  grid: { left: 20, right: 20, top: 22, bottom: 42 },
  xAxis: {
    type: "category",
    data: phases.map((phase) => phase.phase.toUpperCase()),
    axisLabel: { color: PAPER, fontWeight: 800 },
    axisLine: { lineStyle: { color: MUTED } },
    axisTick: { show: false },
  },
  yAxis: {
    type: "category",
    data: ["ERROR RATE"],
    axisLabel: { color: MUTED },
    axisLine: { show: false },
    axisTick: { show: false },
  },
  visualMap: {
    show: false,
    min: 0,
    max: Math.max(1, ...phases.map((phase) => percent(phase.rate?.mean ?? 0))),
    inRange: { color: [PAPER, MUTED, RED] },
  },
  series: [
    {
      name: "Error rate",
      type: "heatmap",
      data: phases.map((phase, index) => ({
        phase: phase.phase.toUpperCase(),
        value: [index, 0, percent(phase.rate?.mean ?? 0)],
        decisions: phase.decisions,
        games: phase.games,
        interval: phase.rate ? [percent(phase.rate.interval.low), percent(phase.rate.interval.high)] : null,
      })),
      label: {
        show: true,
        color: INK,
        fontWeight: 900,
        formatter: (item: { value: [number, number, number] }) => `${item.value[2]}%`,
      },
      itemStyle: { borderColor: INK, borderWidth: 4 },
    },
  ],
});

export interface ActivityWeek {
  weekStart: string;
  games: number;
}
export const buildActivityOption = (weeks: ActivityWeek[]): EChartsCoreOption => ({
  ...base("Games played by week. Height means activity, not playing strength."),
  tooltip: {
    ...(base("activity").tooltip as object),
    trigger: "item",
    formatter: (raw: unknown) => {
      const data = tooltipData(raw);
      return `Week of ${date(tooltipText(data.weekStart ?? ""))}<br/>${tooltipText(data.value ?? 0)} games`;
    },
  },
  grid: { left: 42, right: 20, top: 20, bottom: 58 },
  xAxis: {
    type: "category",
    data: weeks.map((week) => week.weekStart),
    axisLine: { lineStyle: { color: MUTED } },
    axisTick: { show: false },
    axisLabel: {
      color: MUTED,
      interval: "auto",
      hideOverlap: true,
      formatter: (value: string) => date(value).replace(/, \d{4}$/, ""),
    },
  },
  yAxis: {
    type: "value",
    minInterval: 1,
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: MUTED },
    splitLine: { show: false },
  },
  series: [
    {
      name: "Games",
      type: "bar",
      barMaxWidth: 18,
      data: weeks.map((week) => ({
        weekStart: week.weekStart,
        value: week.games,
        itemStyle: { color: week.games === Math.max(...weeks.map(({ games }) => games)) ? RED : MUTED },
      })),
      itemStyle: { borderRadius: 0 },
      emphasis: { itemStyle: { color: PAPER } },
    },
  ],
});

export interface ActivityBucket {
  label: string;
  games: number;
  detail?: string;
}
export const buildActivityDistributionOption = (label: string, buckets: ActivityBucket[]): EChartsCoreOption => ({
  ...base(`${label}. Height means games played, not strength.`),
  tooltip: {
    ...(base(label).tooltip as object),
    trigger: "item",
    formatter: (raw: unknown) => {
      const data = tooltipData(raw);
      return `${tooltipText(data.label ?? "")}<br/>${tooltipText(data.value ?? 0)} games${data.detail ? `<br/>${tooltipText(data.detail)}` : ""}`;
    },
  },
  grid: { left: 42, right: 14, top: 20, bottom: 48 },
  xAxis: {
    type: "category",
    data: buckets.map(({ label: bucket }) => bucket),
    axisLine: { lineStyle: { color: MUTED } },
    axisTick: { show: false },
    axisLabel: { color: MUTED, fontSize: 10 },
  },
  yAxis: {
    type: "value",
    minInterval: 1,
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: MUTED },
    splitLine: { show: false },
  },
  series: [
    {
      name: "Games",
      type: "bar",
      barMaxWidth: 30,
      data: buckets.map((bucket) => ({
        ...bucket,
        value: bucket.games,
        itemStyle: {
          color: bucket.games === Math.max(...buckets.map(({ games }) => games)) && bucket.games > 0 ? RED : MUTED,
        },
      })),
      itemStyle: { borderRadius: 0 },
      emphasis: { itemStyle: { color: PAPER } },
    },
  ],
});

export interface RepertoireDatum {
  opening: string;
  color: "white" | "black";
  games: number;
  share: number;
  score: number;
}
export const buildRepertoireOption = (openings: RepertoireDatum[]): EChartsCoreOption => {
  const largest = openings.reduce(
    (best, opening, index) => (best < 0 || opening.games > openings[best].games ? index : best),
    -1,
  );
  return {
    ...base("Opening repertoire by game count. Each rectangle reports color, games, share, and score."),
    tooltip: {
      ...(base("repertoire").tooltip as object),
      trigger: "item",
      formatter: (raw: unknown) => {
        const item = tooltipData(raw);
        return `${tooltipText(item.name ?? "Opening")}<br/>${tooltipText(item.value ?? 0)} games · ${tooltipText(item.share ?? 0)}% of scope<br/>Score: ${tooltipText(item.score ?? 0)}%`;
      },
    },
    series: [
      {
        type: "treemap",
        roam: false,
        nodeClick: false,
        breadcrumb: { show: false },
        data: openings.map((opening, index) => {
          const color = index === largest ? RED : opening.color === "white" ? PAPER : CHARCOAL;
          return {
            name: conciseOpeningName(opening.opening).toUpperCase(),
            side: opening.color === "white" ? "White" : "Black",
            value: opening.games,
            share: percent(opening.share),
            score: percent(opening.score),
            itemStyle: { color, borderColor: color, borderWidth: 3 },
            label: { color: color === PAPER ? INK : PAPER, fontWeight: 900 },
          };
        }),
      },
    ],
  };
};

type ObservedTimeUse = Extract<TimeUse, { status: "observed" }>;
export const buildThinkTimeOption = ({
  instantSeconds,
  longThinkSeconds,
  bands,
}: Pick<ObservedTimeUse, "instantSeconds" | "longThinkSeconds" | "bands">): EChartsCoreOption => {
  const labels = {
    instant: `QUICK ≤${instantSeconds}s`,
    normal: "NORMAL",
    long: `LONG ≥${Math.round(longThinkSeconds)}s`,
  };
  const rates = bands.map((band) => (band.errorRate ? percent(band.errorRate.mean) : null));
  const worst = Math.max(...rates.map((rate) => rate ?? -1));
  return {
    ...base("Mistake rate by how long you thought before moving, with each band's share of moves in the tooltip."),
    tooltip: {
      ...(base("think time").tooltip as object),
      trigger: "item",
      formatter: (raw: unknown) => {
        const item = tooltipData(raw);
        return `${tooltipText(item.label ?? "")}<br/>${tooltipText(item.share ?? 0)}% of your moves<br/>Mistakes: ${tooltipText(item.events ?? 0)} of ${tooltipText(item.total ?? 0)}`;
      },
    },
    grid: { left: 8, right: 8, top: 28, bottom: 28 },
    xAxis: {
      type: "category",
      data: bands.map((band) => labels[band.band]),
      axisLabel: { color: PAPER, fontWeight: 800, interval: 0 },
      axisLine: { lineStyle: { color: MUTED } },
      axisTick: { show: false },
    },
    yAxis: { type: "value", min: 0, max: Math.max(10, worst), show: false },
    series: [
      {
        name: "Mistake rate",
        type: "bar",
        barWidth: "46%",
        data: bands.map((band, index) => ({
          label: labels[band.band],
          value: rates[index],
          share: percent(band.share),
          events: band.errorRate?.events ?? 0,
          total: band.decisions,
          itemStyle: { color: rates[index] != null && rates[index] === worst ? RED : PAPER },
        })),
        label: { show: true, position: "top", color: PAPER, fontWeight: 900, formatter: "{c}%" },
      },
    ],
  };
};

export const buildThinkPhaseOption = (phases: ObservedTimeUse["phases"]): EChartsCoreOption => {
  const rows = phases.filter((phase) => phase.averageSeconds != null);
  return {
    ...base("Average seconds spent per move in the opening, middlegame, and endgame."),
    tooltip: {
      ...(base("phase think").tooltip as object),
      trigger: "item",
      formatter: (raw: unknown) => {
        const item = tooltipData(raw);
        return `${tooltipText(item.phase ?? "")}<br/>${tooltipText(item.value ?? 0)}s average<br/>${tooltipText(item.decisions ?? 0)} moves`;
      },
    },
    grid: { left: 96, right: 40, top: 8, bottom: 8 },
    xAxis: { type: "value", show: false },
    yAxis: {
      type: "category",
      inverse: true,
      data: rows.map((phase) => phase.phase.toUpperCase()),
      axisLabel: { color: PAPER, fontWeight: 800 },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    series: [
      {
        name: "Average think",
        type: "bar",
        barWidth: "55%",
        data: rows.map((phase) => ({
          phase: phase.phase.toUpperCase(),
          value: Math.round(phase.averageSeconds!),
          decisions: phase.decisions,
        })),
        itemStyle: { color: MUTED },
        label: { show: true, position: "right", color: PAPER, fontWeight: 900, formatter: "{c}s" },
      },
    ],
  };
};
